import axios from 'axios'
import logger from '@/utils/logger'

const state = {
  customers: [],
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1
  },
  currentCustomer: null,
  loading: false,
  error: null
}

const mutations = {
  SET_CUSTOMERS(state, { customers, pagination }) {
    state.customers = customers
    state.pagination = pagination
  },

  SET_CURRENT_CUSTOMER(state, customer) {
    state.currentCustomer = customer
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  ADD_CUSTOMER(state, customer) {
    state.customers.unshift(customer)
    state.pagination.total += 1
  },

  UPDATE_CUSTOMER(state, updatedCustomer) {
    const index = state.customers.findIndex(c => c.id === updatedCustomer.id)
    if (index !== -1) {
      state.customers.splice(index, 1, updatedCustomer)
    }
    if (state.currentCustomer?.id === updatedCustomer.id) {
      state.currentCustomer = updatedCustomer
    }
  },

  DELETE_CUSTOMER(state, customerId) {
    state.customers = state.customers.filter(c => c.id !== customerId)
    state.pagination.total -= 1
  }
}

const actions = {
  async fetchCustomers({ commit, rootState }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const user = rootState.auth.user

      // Automatically filter by sales person if they are not admin/assistant
      if (user?.role?.name === '销售') {
        params.advisor_id = user.id
      }

      const response = await axios.get('/customers/', { params })
      commit('SET_CUSTOMERS', response.data)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch customers from store:', error)

      // Handle authentication errors gracefully
      if (error.response?.status === 401) {
        logger.warn('Authentication failed when fetching customers from store')
        commit('SET_CUSTOMERS', { customers: [], pagination: {
          page: 1,
          per_page: 20,
          total: 0,
          pages: 1
        }})
        return { customers: [], pagination: {
          page: 1,
          per_page: 20,
          total: 0,
          pages: 1
        }}
      }

      const errorMessage = error.response?.data?.message || '获取客户列表失败'
      commit('SET_ERROR', errorMessage)
      commit('SET_CUSTOMERS', { customers: [], pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        pages: 1
      }})
      return { customers: [], pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        pages: 1
      }}
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchCustomer({ commit }, customerId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.get(`/customers/${customerId}`)
      commit('SET_CURRENT_CUSTOMER', response.data.customer)
      return response.data.customer
    } catch (error) {
      logger.error('Failed to fetch customer from store:', error)

      // Handle authentication errors gracefully
      if (error.response?.status === 401) {
        logger.warn('Authentication failed when fetching customer from store')
        commit('SET_CURRENT_CUSTOMER', null)
        return null
      }

      const errorMessage = error.response?.data?.message || '获取客户详情失败'
      commit('SET_ERROR', errorMessage)
      commit('SET_CURRENT_CUSTOMER', null)
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createCustomer({ commit }, customerData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.post('/customers', customerData)
      commit('ADD_CUSTOMER', response.data.customer)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建客户失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateCustomer({ commit }, { customerId, customerData }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.put(`/customers/${customerId}`, customerData)
      commit('UPDATE_CUSTOMER', response.data.customer)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新客户失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteCustomer({ commit }, customerId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      await axios.delete(`/customers/${customerId}`)
      commit('DELETE_CUSTOMER', customerId)
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除客户失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  fetchCustomersByPhone({ dispatch }, phone) {
    return dispatch('fetchCustomers', { phone })
  },

  searchCustomers({ dispatch }, searchTerm) {
    return dispatch('fetchCustomers', { search: searchTerm })
  },

  async exportCustomers({ rootState }, params = {}) {
    const user = rootState.auth.user
    if (user?.role?.name === '销售') {
      params.advisor_id = user.id
    }

    const response = await axios.get('/customers/export', {
      params,
      responseType: 'blob'
    })

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `customers_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return { success: true }
  }
}

const getters = {
  allCustomers: state => state.customers,
  paginatedCustomers: state => state.customers,
  pagination: state => state.pagination,
  currentCustomer: state => state.currentCustomer,
  isLoading: state => state.loading,
  error: state => state.error,

  customersByCategory: (state) => (category) => {
    return state.customers.filter(customer => customer.category === category)
  },

  overdueCustomers: (state) => {
    return state.customers.filter(customer => customer.overdue_status === true)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}